'use client';

import { c as _c } from "react/compiler-runtime";
import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import { useState } from 'react';
import { toTextPreview } from '@/app/donation/_utils/text';
import { Image } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';
interface ReviewCardProps {
  rating: number;
  createdAt: string;
  productName: string;
  contents: string;
  /** 작성자 닉네임. `headerVariant="nickname"`일 때만 표시되며, 결측(null)이면 날짜만 렌더한다 */
  reviewer?: string | null;
  /**
   * 헤더 레이아웃 종류. `reviewer` 유무로 암묵 분기하면 닉네임이 없는 카드만
   * 다른 레이아웃·날짜 포맷이 되어 같은 목록 안에서 카드가 섞이므로 항상 명시한다.
   * - 'nickname': 닉네임 + 컴팩트 날짜(YY.MM.DD), actions 우상단 절대배치
   * - 'inline': 평점+날짜 한 줄, actions 같은 줄 우측(YYYY.MM.DD)
   * @default 'nickname'
   */
  headerVariant?: 'nickname' | 'inline';
  imageUrlList: string[];
  answer?: string | null;
  answerAt?: string | null;
  /** 추가 액션 요소 (드롭다운 메뉴 등), 오른쪽 상단에 렌더링 */
  actions?: React.ReactNode;
  /** 이미지 클릭 콜백 */
  onImageClick?: () => void;
  /**
   * 카드 전체 클릭 콜백. 카드를 `<button>`으로 감싸지 말고 이 prop을 쓴다 —
   * 카드 안에는 `actions`·본문 더보기·답변 더보기가 이미 인터랙티브라
   * 외부에서 감싸면 중첩 인터랙티브(invalid HTML·hydration 에러)가 되고
   * 내부 컨트롤을 키보드로 조작할 수 없다.
   */
  onSelect?: () => void;
  isTrialReview?: boolean;
}
export const ReviewCard = t0 => {
  const $ = _c(49);
  const {
    rating,
    createdAt,
    productName,
    contents: t1,
    reviewer,
    headerVariant: t2,
    imageUrlList: t3,
    answer,
    answerAt,
    actions,
    onImageClick,
    onSelect,
    isTrialReview
  } = t0;
  const contents = t1 === undefined ? "" : t1;
  const headerVariant = t2 === undefined ? "nickname" : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const imageUrlList = t4;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps"
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [emblaRef] = useEmblaCarousel(t5);
  let t6;
  if ($[3] !== contents) {
    t6 = toTextPreview(contents, ContentsExpandedPoint);
    $[3] = contents;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const contentsPreview = t6;
  const t7 = answer ?? "";
  let t8;
  if ($[5] !== t7) {
    t8 = toTextPreview(t7, AnswerExpandedPoint);
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const answerPreview = t8;
  const t9 = onSelect && "transition-opacity duration-200 ease-out";
  const t10 = onSelect && "hover:opacity-80";
  const t11 = onSelect && "active:opacity-80";
  let t12;
  if ($[7] !== t10 || $[8] !== t11 || $[9] !== t9) {
    t12 = cn("relative border-t-1 border-Gray-200 p-20 text-left first:border-t-0", "mobile-tablet:border-t-0 mobile-tablet:py-24", t9, t10, t11);
    $[7] = t10;
    $[8] = t11;
    $[9] = t9;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== onSelect || $[12] !== productName) {
    t13 = onSelect ? <button onClick={onSelect} aria-label={`${productName} 후기 상세 보기`} className={cn("absolute inset-0 rounded-large", "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-B-500")} /> : null;
    $[11] = onSelect;
    $[12] = productName;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== actions || $[15] !== createdAt || $[16] !== headerVariant || $[17] !== rating || $[18] !== reviewer) {
    t14 = headerVariant === "nickname" ? <>{actions ? <div className="absolute right-20 top-20 z-10">{actions}</div> : null}<Rating value={rating} size={16} className="mb-8" /><div className="mb-6 flex items-center gap-8">{reviewer ? <span className="text-Gray-550 typo-caption1">{reviewer}</span> : null}{reviewer && createdAt ? <div className="h-10 border-l-1 border-Gray-300" /> : null}{createdAt ? <span className="text-Gray-550 typo-caption1">{dayjs(createdAt).format("YYYY.MM.DD")}</span> : null}</div></> : <div className="mb-8 flex items-center gap-8"><Rating value={rating} size={16} />{createdAt ? <span className="text-Gray-550 typo-caption1">{dayjs(createdAt).format("YYYY.MM.DD")}</span> : null}{actions ? <div className="relative z-10 ml-auto">{actions}</div> : null}</div>;
    $[14] = actions;
    $[15] = createdAt;
    $[16] = headerVariant;
    $[17] = rating;
    $[18] = reviewer;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== productName) {
    t15 = <p className="mb-16 font-400 text-Gray-600 typo-body4">{productName}</p>;
    $[20] = productName;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== emblaRef || $[23] !== imageUrlList || $[24] !== onImageClick || $[25] !== productName) {
    t16 = imageUrlList.length > 0 && <div className="mb-16"><div className="relative w-full overflow-hidden" ref={emblaRef}><div className="flex gap-8">{imageUrlList.map((url, index) => <div key={index} className={cn("w-auto flex-[0_0_auto]", onImageClick && "relative z-10 cursor-pointer")} onClick={onImageClick}><Image src={url} alt={`${productName}-${index + 1}`} width={128} height={128} className="block aspect-square rounded-large border border-Gray-black/5 object-cover" /></div>)}</div></div></div>;
    $[22] = emblaRef;
    $[23] = imageUrlList;
    $[24] = onImageClick;
    $[25] = productName;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== contentsPreview.isTruncated || $[28] !== contentsPreview.preview || $[29] !== contentsPreview.text || $[30] !== isExpanded) {
    t17 = contentsPreview.isTruncated ? <p className={cn("whitespace-pre-line font-500 text-Gray-800 typo-body3", "mobile-tablet:font-400 mobile-tablet:text-Gray-900 mobile-tablet:typo-body4")}>{isExpanded ? contentsPreview.text : `${contentsPreview.preview}...`}{!isExpanded && <span role="button" tabIndex={0} onClick={e => {
        e.stopPropagation();
        setIsExpanded(true);
      }} onKeyDown={e_0 => {
        if (e_0.key === "Enter" || e_0.key === " ") {
          e_0.preventDefault();
          e_0.stopPropagation();
          setIsExpanded(true);
        }
      }} className="relative z-10 inline-block cursor-pointer whitespace-nowrap px-4 font-600 text-Gray-550 typo-body3">더보기</span>}</p> : <p className={cn("whitespace-pre-line font-500 text-Gray-800 typo-body3", "mobile-tablet:font-400 mobile-tablet:text-Gray-900 mobile-tablet:typo-body4")}>{contentsPreview.text}</p>;
    $[27] = contentsPreview.isTruncated;
    $[28] = contentsPreview.preview;
    $[29] = contentsPreview.text;
    $[30] = isExpanded;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== isTrialReview) {
    t18 = isTrialReview ? <p className="mt-12 font-400 text-Gray-550 typo-caption1">{AMBASSADOR_NOTICE}</p> : null;
    $[32] = isTrialReview;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== answerAt || $[35] !== answerPreview.isTruncated || $[36] !== answerPreview.preview || $[37] !== answerPreview.text || $[38] !== isAnswerExpanded) {
    t19 = answerPreview.text ? <div className="mt-16 flex w-full flex-col rounded-medium bg-Gray-25 p-20"><div className="flex items-center justify-between pb-4"><p className="font-600 text-Gray-700 typo-body4">판매자</p>{answerAt ? <p className="font-400 text-Gray-550 typo-caption1">{dayjs(answerAt).format("YYYY.MM.DD")}</p> : null}</div><p className="whitespace-pre-line pb-8 font-400 text-Gray-700 typo-body4">{answerPreview.isTruncated ? <>{isAnswerExpanded ? answerPreview.text : `${answerPreview.preview}...`}</> : answerPreview.text}</p>{answerPreview.isTruncated && <Button className="relative z-10 mt-8 size-fit p-0" theme="sub-text" asChild={true}><div className="flex cursor-pointer items-center gap-4" onClick={e_1 => {
          e_1.stopPropagation();
          setIsAnswerExpanded(_temp);
        }}><p className="font-600 text-Gray-550 typo-caption1">{isAnswerExpanded ? "\uC811\uAE30" : "\uB354\uBCF4\uAE30"}</p><SvgChevrondown className={cn("size-12 text-Gray-550 transition-transform duration-300", {
            "-rotate-180": isAnswerExpanded
          })} /></div></Button>}</div> : null;
    $[34] = answerAt;
    $[35] = answerPreview.isTruncated;
    $[36] = answerPreview.preview;
    $[37] = answerPreview.text;
    $[38] = isAnswerExpanded;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15 || $[44] !== t16 || $[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <div className={t12}>{t13}{t14}{t15}{t16}{t17}{t18}{t19}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
};
const AnswerExpandedPoint = 60;
const ContentsExpandedPoint = 70;
export const AMBASSADOR_NOTICE = '웰로 체험단이 상품을 제공받아 직접 체험 후 작성한 후기입니다.';
function _temp(prev) {
  return !prev;
}